import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { Button, CircularProgress, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import moment from "moment";
import { get } from "lodash";

import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import { getOptionValue, paymentModeEnum } from "common/enums";
import Backdrop from "Components/Common/Backdrop";
import DatePicker from "Components/Common/DatePicker";
import DateField from "Components/Common/DatePicker";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { dateFormatter } from "utils/dateFormatter";
import { onlyNumberKeys } from "utils";

import { useGetDemandEstimationByDemandEstimateIdQuery } from "apis/DemandEstimation";
import { usePutPaymentHistoryFileMutation } from "apis/Payment";

const PaymentDetailsEditModal = ({
  editModelData: {
    id,
    assignment,
    transactionDate,
    amount,
    paymentMode: currentPaymentMode,
    glAccount,
    tds,
    moduleTypeId,
    moduleType,
    isPowerUtility,
    isOtherUtilitySubscription,
    gstAmount: { cgstAmount: cgst, igstAmount: igst, sgstAmount: sgst },
  },
  handleClose,
  t,
}) => {
  const [updatePaymentDetails, { isLoading }] =
    usePutPaymentHistoryFileMutation();

  const [
    getDemandEstimateData,
    {
      data: { data: { charges } = {} } = {},
      isLoading: isDemandEstimateDataLoading,
    },
  ] = useGetDemandEstimationByDemandEstimateIdQuery();

  const chargesInitialValues = useMemo(
    () =>
      charges
        ? Object.entries(charges).reduce((acc, [key, value]) => {
            if (value > 0 && get(tds, `${key}TDS.year`)) {
              acc[key] = value;
              acc[`${key}Tds`] = get(tds, `${key}TDS.amount`);
              if (isPowerUtility) {
                acc.tdsYear = moment(get(tds, `${key}TDS.year`).split("-")[0]);
              } else {
                acc[`${key}TdsYear`] = moment(
                  get(tds, `${key}TDS.year`).split("-")[0]
                );
              }
            }
            return acc;
          }, {})
        : {
            tdsYear: tds.tdsAmount?.year
              ? moment(tds.tdsAmount.year.split("-")[0])
              : moment(),
            tdsAmount: tds.tdsAmount?.amount ?? "",
            ...(isOtherUtilitySubscription && {
              cgstAmount: cgst ?? 0,
              igstAmount: igst ?? 0,
              sgstAmount: sgst ?? 0,
              cgstTdsAmount: tds.cgsttdsAmount.amount ?? 0,
              igstTdsAmount: tds.igsttdsAmount.amount ?? 0,
              sgstTdsAmount: tds.sgsttdsAmount.amount ?? 0,
              cgstTdsYear: tds.cgsttdsAmount?.year
                ? moment(tds.cgsttdsAmount.year.split("-")[0])
                : "",
              igstTdsYear: tds.igsttdsAmount?.year
                ? moment(tds.igsttdsAmount.year.split("-")[0])
                : "",
              sgstTdsYear: tds.sgsttdsAmount?.year
                ? moment(tds.sgsttdsAmount.year.split("-")[0])
                : "",
            }),
          },
    [charges]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id,
      bankRefNumber: assignment ?? "",
      amount: amount - ((cgst ?? 0) + (sgst ?? 0) + (igst ?? 0)) ?? "",
      transactionDate: moment(transactionDate),
      paymentMode: getOptionValue("paymentModeEnum", currentPaymentMode),
      glAccount: glAccount ?? "",
      tds: chargesInitialValues,
    },
    validationSchema: Yup.object({
      paymentMode: Yup.string().required("Payment mode is required"),
      bankRefNumber: Yup.string().test(
        "isValidBankRefNumberRequired",
        "",
        (_, context) => {
          const [{ value: { bankRefNumber, paymentMode } = {} } = {}] =
            context.from || [];

          if (
            paymentMode !== getOptionValue("paymentModeEnum", "Cash") &&
            !bankRefNumber
          ) {
            return context.createError({
              message: "Bank reference Number is required",
            });
          }
          return true;
        }
      ),

      amount: Yup.number().required("Amount is required"),
      transactionDate: Yup.date()
        .required("Transaction date is required")
        .test("isValidTransactionDateDate", "Enter past date", (_, context) => {
          const [{ value: { transactionDate } = {} } = {}] = context.from || [];

          if (!transactionDate) {
            return context.createError({ message: "Enter a valid date" });
          }
          return transactionDate.isSameOrBefore(moment());
        }),
      tds: Yup.object(
        charges
          ? Object.entries(charges).reduce((acc, [key, value]) => {
              if (value > 0 && get(tds, `${key}TDS.year`)) {
                const capitalizeKey = convertCamelTextToCapitalizeText(key);

                acc[`${key}Tds`] = Yup.number()
                  .required(`${capitalizeKey} TDS amount is required`)
                  .test(`isValid${key}TDSAmount`, "", function () {
                    const [{ value: current = null } = {}] = this.from || [];

                    if (current[`${key}Tds`] < 0) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must greater than 0`,
                      });
                    }

                    if (current[key] < current[`${key}Tds`]) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must be less than ${capitalizeKey}`,
                      });
                    }

                    return true;
                  });
                if (!isPowerUtility) {
                  acc[`${key}TdsYear`] = Yup.date().test(
                    "isValidYear",
                    "",
                    function () {
                      const [{ value: current = {} } = {}] = this.from || [];
                      if (!current[`${key}TdsYear`]) {
                        return this.createError({
                          message: "Select financial year",
                        });
                      }
                      return true;
                    }
                  );
                }
              }
              return acc;
            }, {})
          : {
              tdsAmount: Yup.number().test("isValidTDSAmount", "", function () {
                const [
                  { value: current = null } = {},
                  { value: parent = null } = {},
                ] = this.from || [];

                if (
                  moduleType !==
                    getOptionValue("moduleTypes", "Demand Estimate") &&
                  !current.tdsAmount &&
                  current.tdsAmount !== 0
                ) {
                  return this.createError({
                    message: "TDS amount is required",
                  });
                }

                if (current.tdsAmount > parent.amount) {
                  return this.createError({
                    message: "TDS amount must be less than amount",
                  });
                }

                return true;
              }),
              ...(isOtherUtilitySubscription && {
                ...(!!cgst && {
                  cgstAmount: Yup.number().required("CGST amount is required"),
                  cgstTdsAmount: Yup.number()
                    .required("CGST TDS amount is required")
                    .test("isValidCgstTDSAmount", "", function () {
                      const [{ value: current = null } = {}] = this.from || [];
                      if (current.cgstTdsAmount > current.cgstAmount) {
                        return this.createError({
                          message:
                            "CGST TDS amount must be less than CGST amount",
                        });
                      }
                      return true;
                    }),
                }),
                ...(!!igst && {
                  igstAmount: Yup.number().required("IGST amount is required"),
                  igstTdsAmount: Yup.number()
                    .required("IGST TDS amount is required")
                    .test("isValidIgstTDSAmount", "", function () {
                      const [{ value: current = null } = {}] = this.from || [];
                      if (current.igstTdsAmount > current.igstAmount) {
                        return this.createError({
                          message:
                            "IGST TDS amount must be less than IGST amount",
                        });
                      }

                      return true;
                    }),
                }),
                ...(!!sgst && {
                  sgstAmount: Yup.number().required("SGST amount is required"),
                  sgstTdsAmount: Yup.number()
                    .required("SGST TDS amount is required")
                    .test("isValidSgstTDSAmount", "", function () {
                      const [{ value: current = null } = {}] = this.from || [];
                      if (current.sgstTdsAmount > current.sgstAmount) {
                        return this.createError({
                          message:
                            "SGST TDS amount must be less than SGST amount",
                        });
                      }
                      return true;
                    }),
                }),
              }),
            }
      ),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        let tdsDetails = { ...values.tds };
        if (
          charges &&
          moduleType === getOptionValue("moduleTypes", "Demand Estimate")
        ) {
          Object.entries(charges).forEach(([key, value]) => {
            if (value > 0 && get(tds, `${key}TDS.year`)) {
              tdsDetails[`${key}TdsYear`] = isPowerUtility
                ? values.tds.tdsYear.format("YYYY")
                : get(values.tds, `${key}TdsYear`).format("YYYY");
            }
          });
        } else {
          tdsDetails = {
            ...tdsDetails,
            tdsYear: tdsDetails.tdsYear.format("YYYY"),
            ...(isOtherUtilitySubscription && {
              cgstAmount: values.tds.cgstAmount,
              cgstTdsAmount: values.tds.cgstTdsAmount || 0,
              cgstTdsYear: values.tds.cgstTdsYear
                ? values.tds.cgstTdsYear.format("YYYY")
                : "",
              igstAmount: values.tds.igstAmount,
              igstTdsAmount: values.tds.igstTdsAmount || 0,
              igstTdsYear: values.tds.igstTdsYear
                ? values.tds.igstTdsYear.format("YYYY")
                : "",
              sgstAmount: values.tds.sgstAmount,
              sgstTdsAmount: values.tds.sgstTdsAmount || 0,
              sgstTdsYear: values.tds.sgstTdsYear
                ? values.tds.sgstTdsYear.format("YYYY")
                : "",
            }),
          };
        }
        await updatePaymentDetails({
          ...values,
          transactionDate: values.transactionDate.format("YYYY-MM-DD"),
          tds: { ...tdsDetails },
        });

        resetForm();
        handleClose();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    values: {
      paymentMode,
      tds: { tdsYear, cgstTdsYear, igstTdsYear, sgstTdsYear },
    },
    values,
    setFieldValue,
  } = formik;

  useEffect(() => {
    (async () => {
      if (moduleType === getOptionValue("moduleTypes", "Demand Estimate"))
        await getDemandEstimateData(moduleTypeId);
    })();
  }, [moduleType, moduleTypeId]);

  if (isDemandEstimateDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      maxWidth="md"
      title="Update Payment Details"
    >
      <Grid
        container
        rowSpacing={1}
        className="mt-1"
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      >
        <Grid item xs={12} sm={6} md={4}>
          <SelectField
            label={t("Payment Mode") + "*"}
            name="paymentMode"
            onChange={(_, { value }) => {
              setFieldValue("paymentMode", value);

              if (value === getOptionValue("paymentModeEnum", "Cash")) {
                setFieldValue("bankRefNumber", "");
              }
            }}
            formik={formik}
            options={paymentModeEnum || []}
          />
        </Grid>
        {paymentMode !== getOptionValue("paymentModeEnum", "Cash") && (
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Reference Number"
              name="bankRefNumber"
              formik={formik}
              placeholder="Enter Reference Number"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label="GL account"
            name="glAccount"
            formik={formik}
            placeholder="Enter GL Account"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DateField
            label={t("Transaction Date") + "*"}
            format="DD-MM-YYYY"
            name="transactionDate"
            maxDate={moment()}
            formik={formik}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label="Amount"
            name="amount"
            formik={formik}
            type="number"
            placeholder="Enter Amount"
            InputProps={{
              inputProps: {
                min: 0,
                onKeyDown: (e) => onlyNumberKeys(e, true),
              },
            }}
          />
        </Grid>
        {moduleType !== getOptionValue("moduleTypes", "Demand Estimate") && (
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="TDS Amount"
              name="tds.tdsAmount"
              formik={formik}
              type="number"
              placeholder="Enter TDS Amount"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        )}
        {(isPowerUtility || isOtherUtilitySubscription) && (
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="tds.tdsYear"
              formik={formik}
              label={t("Financial Year*")}
              views={["year"]}
              format={dateFormatter(tdsYear)}
            />
          </Grid>
        )}
        {isOtherUtilitySubscription && (
          <>
            {!!cgst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="CGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.cgstAmount"
                    placeholder="Enter CGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="CGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.cgstTdsAmount"
                    placeholder="Enter CGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="CGST Financial Year"
                    name="tds.cgstTdsYear"
                    formik={formik}
                    label={t("CGST Financial Year")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(cgstTdsYear)}
                  />
                </Grid>
              </>
            )}
            {!!igst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="IGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.igstAmount"
                    placeholder="Enter IGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="IGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.igstTdsAmount"
                    placeholder="Enter IGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="IGST Financial Year"
                    name="tds.igstTdsYear"
                    formik={formik}
                    label={t("IGST Financial Year")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(igstTdsYear)}
                  />
                </Grid>
              </>
            )}
            {!!sgst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="SGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.sgstAmount"
                    placeholder="Enter SGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="SGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="tds.sgstTdsAmount"
                    placeholder="Enter SGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="SGST Financial Year"
                    name="tds.sgstTdsYear"
                    formik={formik}
                    label={t("SGST Financial Year")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(sgstTdsYear)}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        {!!charges && (
          <Grid
            container
            item
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            {Object.entries(charges).map(([key, value]) => (
              <>
                {value > 0 && !!get(tds, `${key}TDS.year`) && (
                  <>
                    <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                      <InputFieldText
                        label={`${convertCamelTextToCapitalizeText(key)}*`}
                        type="number"
                        InputProps={{
                          inputProps: {
                            min: 0,
                            onKeyDown: (e) => onlyNumberKeys(e, true),
                          },
                        }}
                        formik={formik}
                        name={`tds.${key}`}
                        placeholder={`Enter ${convertCamelTextToCapitalizeText(
                          key
                        )}`}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                      <InputFieldText
                        label={`${convertCamelTextToCapitalizeText(
                          key
                        )} TDS Amount*`}
                        type="number"
                        InputProps={{
                          inputProps: {
                            min: 0,
                            onKeyDown: (e) => onlyNumberKeys(e, true),
                          },
                        }}
                        formik={formik}
                        name={`tds.${key}Tds`}
                        placeholder={`Enter ${convertCamelTextToCapitalizeText(
                          key
                        )} TDS Amount`}
                      />
                    </Grid>

                    {!isPowerUtility && (
                      <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                        <DatePicker
                          name={`tds.${key}TdsYear`}
                          formik={formik}
                          label={`${convertCamelTextToCapitalizeText(
                            key
                          )} Financial Year*`}
                          views={["year"]}
                          format={dateFormatter(
                            get(values, `tds.${key}TdsYear`)
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </>
            ))}
          </Grid>
        )}
      </Grid>
      <Grid mt={1} className="text-end">
        <Button
          color="primary"
          size="small"
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          Submit
          {isLoading && <CircularProgress size={20} />}
        </Button>
      </Grid>
    </Modal>
  );
};

PaymentDetailsEditModal.propTypes = {
  editModelData: PropTypes.object,
  t: PropTypes.func,
  handleClose: PropTypes.func,
};

export default withTranslation()(PaymentDetailsEditModal);
